import type { ReactNode } from "react";
import { MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { cn } from "@/modern/utils";
import { usePromotePanel } from "../context/PromotePanelContext";

/** Shared chip chrome — h-9 / text-sm matches badge.chip-stat control size. */
const CHIP_BASE =
  "inline-flex h-9 items-center rounded-full border px-3 text-sm font-normal";

/** Mosaic identity soft fills — distinct neighbors, same object hue over time. */
const PHASE_TONE: Record<string, string> = {
  before: "border-[hsl(var(--slate)/0.35)] bg-[hsl(var(--slate)/0.15)] text-[hsl(var(--slate))]",
  during: "border-[hsl(var(--cobalt)/0.35)] bg-[hsl(var(--cobalt)/0.15)] text-[hsl(var(--cobalt))]",
  after: "border-[hsl(var(--ochre)/0.35)] bg-[hsl(var(--ochre)/0.15)] text-[hsl(var(--ochre))]",
};

const PHASE_FALLBACK = PHASE_TONE.during;

const VISIBILITY_PUBLIC =
  "border-[hsl(var(--moss)/0.35)] bg-[hsl(var(--moss)/0.15)] text-[hsl(var(--moss))]";
const VISIBILITY_RESTRICTED =
  "border-[hsl(var(--rose)/0.35)] bg-[hsl(var(--rose)/0.15)] text-[hsl(var(--rose))]";

/**
 * Confirm summary: Answer First caption + light chrome (meta chips, quote, tag pills).
 */
export const PromoteSummaryCard = () => {
  const { form, restrictionReasonOptions, allTags, temporalTags } = usePromotePanel();
  const watchIsRestricted = form.watch("isRestricted");
  const watchTags = form.watch("tags");
  const watchTemporalTagId = form.watch("temporalTagId");
  const watchCaption = form.watch("caption");
  const watchAltText = form.watch("altText");
  const watchRestrictionReason = form.watch("restrictionReason");

  const temporalLabel = temporalTags.find((t) => t.id === watchTemporalTagId)?.label ?? null;
  const selectedSolutionTags = (watchTags || []).flatMap((id) => {
    const tag = allTags.find((t) => t.id === id);
    return tag &&
      !tag.isFolderEligible &&
      tag.type !== MarketingTagType.PROBLEM &&
      tag.type !== MarketingTagType.META
      ? [tag.label ?? "Untitled"]
      : [];
  });
  const selectedContentRoles = (watchTags || []).flatMap((id) => {
    const tag = allTags.find((t) => t.id === id);
    return tag && tag.isFolderEligible ? [tag.label ?? "Untitled"] : [];
  });
  const restrictionReasonLabel =
    restrictionReasonOptions.find((opt) => opt.value === watchRestrictionReason)?.label ?? null;

  const caption = watchCaption?.trim();
  const altText = watchAltText?.trim();
  const visibilityChip = watchIsRestricted
    ? restrictionReasonLabel
      ? `Restricted · ${restrictionReasonLabel}`
      : "Restricted"
    : "Public eligible";

  const phaseTone = temporalLabel
    ? (PHASE_TONE[temporalLabel.trim().toLowerCase()] ?? PHASE_FALLBACK)
    : PHASE_FALLBACK;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {temporalLabel ? <SummaryChip tone={phaseTone}>{temporalLabel}</SummaryChip> : null}
        <SummaryChip tone={watchIsRestricted ? VISIBILITY_RESTRICTED : VISIBILITY_PUBLIC}>
          {visibilityChip}
        </SummaryChip>
      </div>

      <div>
        {caption ? (
          <p className="text-lg font-normal leading-relaxed text-foreground/90">“{caption}”</p>
        ) : (
          <p className="text-sm font-normal text-muted-foreground">No caption was provided.</p>
        )}
      </div>

      {altText ? (
        <div className="space-y-1">
          <p className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
            Accessibility
          </p>
          <p className="text-sm font-normal leading-relaxed text-muted-foreground">{altText}</p>
        </div>
      ) : null}

      {(selectedSolutionTags.length > 0 || selectedContentRoles.length > 0) && (
        <>
          <div className="h-px bg-border/40" aria-hidden />

          <div className="space-y-3">
            {selectedSolutionTags.length > 0 ? (
              <ChipBand lead="Solution Tags">
                {selectedSolutionTags.map((label) => (
                  <SummaryChip key={`tag-${label}`} tone="border-border bg-muted text-foreground">
                    {label}
                  </SummaryChip>
                ))}
              </ChipBand>
            ) : null}

            {selectedContentRoles.length > 0 ? (
              <ChipBand lead="Roles">
                {selectedContentRoles.map((label) => (
                  <SummaryChip key={`role-${label}`} tone="border-border bg-muted text-foreground">
                    {label}
                  </SummaryChip>
                ))}
              </ChipBand>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};

function SummaryChip({ children, tone }: { children: ReactNode; tone: string }) {
  return <span className={cn(CHIP_BASE, tone)}>{children}</span>;
}

function ChipBand({ lead, children }: { lead: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-normal text-muted-foreground">{lead}</p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}
